import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Chat about one finding, where the model proposes requests and the tester
 * sends them.
 *
 * The model has no tool that reaches the network. When it wants traffic it
 * calls propose_request, which arrives here as a `proposal` event and renders
 * as an editable request — method, URL, headers, body, all of it changeable.
 * Nothing goes out until Send is pressed, and the orchestrator re-checks the
 * engagement scope against whatever was actually edited rather than against
 * what was proposed.
 *
 * The full exchange is shown and then fed back into the conversation, so the
 * model continues from the real response instead of guessing at one.
 */

function orchBase() {
  // Same rule as AttackPathProxy: the dev server on 3081 talks to the
  // orchestrator directly, everything else goes through nginx.
  return window.location.port === '3081'
    ? 'http://localhost:9100/api'
    : '/orchestrator-api/api';
}

const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

function headersToText(h) {
  return Object.entries(h || {}).map(([k, v]) => `${k}: ${v}`).join('\n');
}

function textToHeaders(text) {
  const out = {};
  (text || '').split('\n').forEach(line => {
    const i = line.indexOf(':');
    if (i > 0) {
      const name = line.slice(0, i).trim();
      const value = line.slice(i + 1).trim();
      if (name) out[name] = value;
    }
  });
  return out;
}

/** An editable request card. Nothing here sends until the tester says so. */
function ProposalCard({ proposal, findingId, onExchange, disabled }) {
  const [method, setMethod] = useState(proposal.method || 'GET');
  const [url, setUrl] = useState(proposal.url || '');
  const [headerText, setHeaderText] = useState(headersToText(proposal.headers));
  const [body, setBody] = useState(proposal.body || '');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(true);

  const send = async () => {
    setSending(true); setError('');
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${orchBase()}/ai/finding-chat/${findingId}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          method, url,
          headers: textToHeaders(headerText),
          body: body || null,
          tool_use_id: proposal.tool_use_id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // The scope refusal is the one worth reading in full: it names what IS
        // in scope, which is usually the thing the tester needs to know.
        setError(data.detail || `Request failed (HTTP ${res.status})`);
        return;
      }
      setResult(data);
      onExchange(data);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSending(false);
    }
  };

  const label = { fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 3 };
  const field = { width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 12,
                  fontFamily: mono, background: 'var(--bg-card)', color: 'var(--text)',
                  border: '1px solid var(--border)' };

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
      background: 'var(--bg-input)', margin: '8px 0',
    }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                 cursor: 'pointer', userSelect: 'none', borderBottom: open ? '1px solid var(--border)' : 'none' }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b' }}>PROPOSED REQUEST</span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', flex: 1, overflow: 'hidden',
                       textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: mono }}>
          {method} {url}
        </span>
        {result && (
          <span style={{ fontSize: 11, fontWeight: 600,
                         color: result.response.status < 400 ? '#16a34a' : '#dc2626' }}>
            {result.response.status}
          </span>
        )}
      </div>

      {open && (
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {proposal.rationale && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {proposal.rationale}
            </div>
          )}
          {proposal.expected_signal && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5,
                          paddingLeft: 10, borderLeft: '2px solid var(--border)' }}>
              <strong>Look for:</strong> {proposal.expected_signal}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 100 }}>
              <div style={label}>Method</div>
              <select value={method} onChange={e => setMethod(e.target.value)}
                disabled={!!result} style={{ ...field, fontFamily: 'inherit' }}>
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].map(m =>
                  <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <div style={label}>URL</div>
              <input value={url} onChange={e => setUrl(e.target.value)}
                disabled={!!result} style={field} />
            </div>
          </div>

          <div>
            <div style={label}>Headers (one per line, name: value)</div>
            <textarea value={headerText} onChange={e => setHeaderText(e.target.value)}
              disabled={!!result} rows={Math.min(6, Math.max(2, headerText.split('\n').length))}
              style={{ ...field, resize: 'vertical' }} />
          </div>

          <div>
            <div style={label}>Body</div>
            <textarea value={body} onChange={e => setBody(e.target.value)}
              disabled={!!result} rows={body ? Math.min(8, body.split('\n').length + 1) : 2}
              placeholder="(empty)" style={{ ...field, resize: 'vertical' }} />
          </div>

          {error && (
            <div style={{ fontSize: 11, padding: '6px 8px', borderRadius: 6, lineHeight: 1.5,
                          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
                          color: '#dc2626' }}>
              {error}
            </div>
          )}

          {!result && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={send} disabled={sending || disabled || !url}
                style={{ padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12,
                         fontWeight: 600, fontFamily: 'inherit', color: '#fff',
                         cursor: (sending || disabled || !url) ? 'not-allowed' : 'pointer',
                         background: (sending || disabled || !url) ? 'var(--text-muted)' : '#f59e0b' }}>
                {sending ? 'Sending…' : 'Send this request'}
              </button>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Edit anything above first. Nothing has been sent.
              </span>
            </div>
          )}

          {result && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
              <div style={{ ...label, marginBottom: 5 }}>
                Response &middot; {result.response.status} {result.response.reason} &middot;{' '}
                {result.response.bytes} bytes &middot; {result.response.elapsed_ms}ms
              </div>
              <pre style={{ margin: 0, padding: 8, borderRadius: 6, maxHeight: 180, overflow: 'auto',
                            fontSize: 11, fontFamily: mono, background: 'var(--bg-card)',
                            border: '1px solid var(--border)', color: 'var(--text-secondary)',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {headersToText(result.response.headers)}
              </pre>
              <pre style={{ margin: '6px 0 0', padding: 8, borderRadius: 6, maxHeight: 260,
                            overflow: 'auto', fontSize: 11, fontFamily: mono,
                            background: 'var(--bg-card)', border: '1px solid var(--border)',
                            color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {result.response.body || '(empty body)'}
                {result.response.truncated ? '\n\n… truncated' : ''}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FindingChat({ finding }) {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState([]);     // {role, content} | {proposal}
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, open]);

  // Only the text turns go back to the model; proposal cards are UI state.
  const history = useCallback(
    () => turns.filter(t => t.role && t.content).map(t => ({ role: t.role, content: t.content })),
    [turns]);

  const ask = useCallback(async (text) => {
    if (!text.trim() || streaming) return;
    setError('');
    const outgoing = [...history(), { role: 'user', content: text }];
    setTurns(t => [...t, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${orchBase()}/ai/finding-chat/${finding.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ messages: outgoing }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch { /* body not json */ }
        throw new Error(detail);
      }

      // SSE over fetch: EventSource cannot POST, so the stream is read by hand.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          const evMatch = chunk.match(/^event: (.+)$/m);
          const dataMatch = chunk.match(/^data: (.+)$/m);
          if (!evMatch || !dataMatch) continue;
          let data;
          try { data = JSON.parse(dataMatch[1]); } catch { continue; }

          if (evMatch[1] === 'token') {
            setTurns(t => {
              const copy = [...t];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant') last.content += data.text;
              return copy;
            });
          } else if (evMatch[1] === 'proposal') {
            setTurns(t => [...t, { proposal: { ...data.request, tool_use_id: data.tool_use_id } }]);
          } else if (evMatch[1] === 'error') {
            setError(data.message || 'The model returned an error');
          }
        }
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setStreaming(false);
      // Drop an assistant turn that produced nothing, so the transcript does
      // not accumulate blank bubbles when a turn ends on a proposal.
      setTurns(t => t.filter((x, i) =>
        !(i === t.length - 1 && x.role === 'assistant' && !x.content)));
    }
  }, [finding.id, history, streaming]);

  // A sent request becomes the next question, so the model reasons from the
  // real response rather than from the one it imagined.
  const onExchange = useCallback((exchange) => {
    const { request, response } = exchange;
    const summary =
      `I sent this request:\n\n${request.method} ${request.url}\n` +
      `${headersToText(request.headers)}\n\n${request.body || ''}\n\n` +
      `Response: HTTP ${response.status} ${response.reason} ` +
      `(${response.bytes} bytes, ${response.elapsed_ms}ms)\n` +
      `${headersToText(response.headers)}\n\n` +
      `${(response.body || '').slice(0, 6000)}` +
      `${response.truncated ? '\n… truncated' : ''}\n\n` +
      `What does this tell us?`;
    ask(summary);
  }, [ask]);

  const SUGGESTIONS = [
    'Is this a false positive?',
    'How would an attacker exploit this?',
    'Propose a request that would confirm it',
    'Draft a remediation for the report',
  ];

  return (
    <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 8,
                  background: 'var(--bg-input)' }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px',
                 cursor: 'pointer', userSelect: 'none',
                 borderBottom: open ? '1px solid var(--border)' : 'none' }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                       letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
          Ask AI about this finding
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', flex: 1 }}>
          {turns.length ? `${turns.filter(t => t.role).length} message(s)`
                        : 'Triage it, dig deeper, or propose a request to confirm it'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--primary)' }}>{open ? 'Hide' : 'Open'}</span>
      </div>

      {open && (
        <div style={{ padding: 12 }}>
          <div style={{ maxHeight: 460, overflowY: 'auto', display: 'flex',
                        flexDirection: 'column', gap: 8, marginBottom: 10 }}>
            {turns.length === 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => ask(s)} disabled={streaming}
                    style={{ padding: '5px 10px', borderRadius: 14, fontSize: 11,
                             fontFamily: 'inherit', cursor: 'pointer',
                             background: 'var(--bg-card)', color: 'var(--text-secondary)',
                             border: '1px solid var(--border)' }}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            {turns.map((t, i) => t.proposal ? (
              <ProposalCard key={i} proposal={t.proposal} findingId={finding.id}
                onExchange={onExchange} disabled={streaming} />
            ) : (
              <div key={i} style={{
                alignSelf: t.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '88%', padding: '7px 10px', borderRadius: 8, fontSize: 13,
                lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                background: t.role === 'user' ? 'var(--primary)' : 'var(--bg-card)',
                color: t.role === 'user' ? '#fff' : 'var(--text)',
                border: t.role === 'user' ? 'none' : '1px solid var(--border)',
              }}>
                {t.content || (streaming ? '…' : '')}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {error && (
            <div style={{ fontSize: 11, padding: '6px 8px', borderRadius: 6, marginBottom: 8,
                          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
                          color: '#dc2626' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const text = input; setInput(''); ask(text);
                }
              }}
              placeholder={streaming ? 'Thinking…' : 'Ask about this finding…'}
              disabled={streaming}
              style={{ flex: 1, padding: '7px 10px', borderRadius: 6, fontSize: 13,
                       fontFamily: 'inherit', background: 'var(--bg-card)',
                       color: 'var(--text)', border: '1px solid var(--border)' }} />
            <button onClick={() => { const text = input; setInput(''); ask(text); }}
              disabled={streaming || !input.trim()}
              style={{ padding: '7px 14px', borderRadius: 6, border: 'none', fontSize: 12,
                       fontWeight: 600, fontFamily: 'inherit', color: '#fff',
                       cursor: (streaming || !input.trim()) ? 'not-allowed' : 'pointer',
                       background: (streaming || !input.trim()) ? 'var(--text-muted)' : 'var(--primary)' }}>
              Send
            </button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>
            The AI cannot send traffic by itself. It can propose a request, which you
            review, edit and send — and the engagement scope is enforced either way.
          </div>
        </div>
      )}
    </div>
  );
}
